import * as React from 'react';

import cn from 'classnames';
import MaskedInput from 'react-text-mask';

import { testAttr } from '@utils';

import { IInputFieldProps } from './types';

import * as s from './InputField.scss';

const Input = ({
	innerRef,
	className,
	autoFocus,
	disabled,
	id,
	isValid,
	maxLength,
	name,
	onBlur,
	onChange,
	onFocus,
	onKeyDown,
	onKeyUp,
	placeholder,
	value,
	dataAttributes,
	autocomplete,
	type,
	style,
}: IInputFieldProps) => (
	<input
		ref={innerRef as React.RefObject<HTMLInputElement>}
		style={style}
		className={
			cn(
				s.inputField,
				{ [s.invalid]: !isValid },
				className,
			)
		}
		disabled={disabled}
		autoFocus={autoFocus}
		id={id}
		maxLength={maxLength}
		name={name}
		onBlur={onBlur}
		onChange={onChange}
		onFocus={onFocus}
		onKeyDown={onKeyDown}
		onKeyUp={onKeyUp}
		placeholder={placeholder}
		value={value}
		autoComplete={autocomplete}
		type={type}
		{...dataAttributes}
	/>
);

const TextArea = ({
	innerRef,
	className,
	autoFocus,
	disabled,
	id,
	isValid,
	maxLength,
	name,
	onBlur,
	onChange,
	onFocus,
	onKeyDown,
	onKeyUp,
	placeholder,
	rows = 10,
	value,
}: IInputFieldProps) => (
	<textarea
		ref={innerRef as React.RefObject<HTMLTextAreaElement>}
		className={
			cn(
				s.inputField,
				s.textArea,
				{ [s.invalid]: !isValid },
				className,
			)
		}
		value={value}
		placeholder={placeholder}
		id={id}
		autoFocus={autoFocus}
		disabled={disabled}
		name={name}
		onBlur={onBlur}
		onChange={onChange}
		onFocus={onFocus}
		onKeyDown={onKeyDown}
		onKeyUp={onKeyUp}
		maxLength={maxLength}
		rows={rows}
	/>
);

const InputMask = ({
	innerRef,
	className,
	autoFocus,
	disabled,
	id,
	isValid,
	maxLength,
	name,
	onBlur,
	onChange,
	onFocus,
	onKeyDown,
	onKeyUp,
	placeholder,
	value,
	mask,
	dataAttributes,
	testId,
	guide,
	keepCharPositions,
	showMask,
	pipe,
	placeholderChar,
	type,
	autocomplete,
}: IInputFieldProps) => (
	<MaskedInput
		ref={innerRef as React.RefObject<MaskedInput>}
		className={
			cn(
				s.inputField,
				{ [s.invalid]: !isValid },
				className,
			)
		}
		disabled={disabled}
		autoFocus={autoFocus}
		id={id}
		maxLength={maxLength}
		name={name}
		onBlur={onBlur}
		onChange={onChange}
		onFocus={onFocus}
		onKeyDown={onKeyDown}
		onKeyUp={onKeyUp}
		placeholder={placeholder}
		value={value}
		mask={mask}
		guide={guide}
		keepCharPositions={keepCharPositions}
		pipe={pipe}
		showMask={showMask}
		placeholderChar={placeholderChar}
		type={type}
		autoComplete={autocomplete}
		{...testAttr(testId)}
		{...dataAttributes}
	/>
);

const isMultiLine = ({ multiline, rows }: IInputFieldProps) => !!multiline || !!rows;

const InputFieldWithoutRef = (props: IInputFieldProps) => {
	return (
		props.mask
			? <InputMask {...props}/>
			: isMultiLine(props)
				? <TextArea {...props}/>
				: <Input {...props}/>
	);
};

export const InputField = React.forwardRef(
	(
		props: IInputFieldProps,
		ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement | MaskedInput>,
	) => <InputFieldWithoutRef innerRef={ref} {...props} />,
);
